/**
 * Mietkonto (`ledger`, Spec 04c, „Mietkonto") — sitzt **unter** der
 * Vertragsliste im Reiter *Geld* eines Mieters (`routes/ObjectDetail.tsx`).
 * Das Mietkonto hängt am Mieter, nicht am Vertrag (Spec 04c, „Das Mietkonto
 * hängt am Mieter"): `GET /tenants/{id}/ledger` umfasst alle Verträge.
 *
 * Fünf Spalten am Schreibtisch (Datum · Vorgang · Soll · Haben · Saldo), als
 * Karte je Buchung unter 768px — fünf Spalten sind auf 380px keine Tabelle
 * (Spec 04c, „Schmale Ansicht"). Eine stornierte Zeile bleibt durchgestrichen
 * stehen und trägt zusätzlich das Wort „storniert": Durchstreichung ist nicht
 * das einzige Signal (`design-system`, „Farbe ist nie das einzige Signal" —
 * dasselbe gilt hier für Typografie, Spec 04c).
 *
 * `can_reverse` kommt vollständig vom Server (I9): Der Storno-Knopf
 * erscheint nur, wenn ihn `POST /reverse` für denselben Aufrufer nicht
 * ablehnen würde — der Client trifft dazu keine eigene Entscheidung.
 *
 * `ledger-print` ruft `window.print()`; das `@media print`-Stylesheet, das
 * Symbolleiste, Kontext-Sidebar und Reiterleiste ausblendet, sitzt an den
 * jeweiligen Stellen selbst (`components/Rail.tsx`, `router.tsx::Sidebar`,
 * `routes/ObjectDetail.tsx`) — hier gibt es dafür nichts zu tun außer den
 * eigenen Knopf beim Drucken auszublenden.
 */

import { MoreHorizontal } from 'lucide-react'
import { useState } from 'react'
import type { CSSProperties } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { PopoverMenu } from '@/components/PopoverMenu'
import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/date'
import { useIsDesktopViewport } from '@/lib/viewport'
import type { LedgerRow } from '@/features/billing/api'
import { useReversePayment, useTenantLedger } from '@/features/billing/api'
import { FIELD_CLASS } from '@/components/FormField'
import { statusBorderClass } from '@/features/billing/status'

interface TenantLedgerProps {
  readonly tenantId: string
}

export function TenantLedger({ tenantId }: TenantLedgerProps) {
  const query = useTenantLedger(tenantId)
  const prepaid = query.data?.prepaid ?? []
  const [error, setError] = useState<string | undefined>(undefined)
  // „Storno-Erfolg" (Spec 04c, „Sprache") — derselbe Streifen wie
  // `payment-confirmation` in `routes/Geld.tsx`, hier lokal: `TenantLedger`
  // sitzt in `routes/ObjectDetail.tsx`, das keinen eigenen Streifen führt.
  const [confirmation, setConfirmation] = useState<string | null>(null)

  return (
    <div data-testid="ledger" className="border-border border-t">
      <div className="flex min-h-(--row-h) items-center justify-between gap-3 px-(--page-x) py-1">
        <h2 className="text-section font-(--font-weight-section) text-text">Mietkonto</h2>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {prepaid.map((item) => (
            <span
              key={item.contract.id}
              data-testid={`ledger-prepaid-${item.contract.id}`}
              title={item.months_display}
              className={`text-label text-text border-l-3 py-0.5 pl-2 ${statusBorderClass('settled')}`}
            >
              {prepaid.length > 1 ? `${item.subject.label} · ` : ''}{item.paid_until_display}
            </span>
          ))}
          <button
            type="button"
            data-testid="ledger-print"
            onClick={() => {
              window.print()
            }}
            className={`text-text print:hidden px-3 ${FIELD_CLASS}`}
          >
            Drucken
          </button>
        </div>
      </div>

      {error !== undefined && (
        <p role="alert" className="text-label text-status-overdue px-(--page-x) pb-2">
          {error}
        </p>
      )}

      <LedgerBody query={query} onError={setError} onReversed={setConfirmation} />

      <ConfirmationStrip
        message={confirmation}
        onDismiss={() => {
          setConfirmation(null)
        }}
        testId="ledger-reverse-confirmation"
      />
    </div>
  )
}

function LedgerBody({
  query,
  onError,
  onReversed,
}: {
  readonly query: ReturnType<typeof useTenantLedger>
  readonly onError: (message: string | undefined) => void
  readonly onReversed: (message: string) => void
}) {
  if (query.isPending) return <SkeletonRows testId="ledger-loading" rows={5} />

  if (query.isError && (query.data === undefined || (query.error instanceof ApiError && (query.error.isForbidden || query.error.isUnauthenticated)))) {
    return (
      <ErrorState
        testId="ledger-error"
        retryTestId="ledger-retry"
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
      />
    )
  }

  if (query.data.items.length === 0) {
    return (
      <EmptyState
        testId="ledger-empty"
        title="Noch keine Buchungen."
        hint="Sollstellungen und Zahlungen dieses Mieters erscheinen hier."
      />
    )
  }

  return (
    <>
      {/* Spaltenköpfe nur am Schreibtisch — bei 380px ersetzt die Kartenform
       * (Spec 04c, „Schmale Ansicht") die Tabelle vollständig. */}
      <div className="text-label text-muted border-border hidden border-b px-(--page-x) py-1 md:flex md:gap-3">
        <span className="w-24">Datum</span>
        <span className="flex-1">Vorgang</span>
        <span className="w-24 text-right">Soll</span>
        <span className="w-24 text-right">Haben</span>
        <span className="w-24 text-right">Saldo</span>
      </div>
      <ul>
        {query.data.items.map((row) => (
          <LedgerRowItem key={row.id} row={row} onError={onError} onReversed={onReversed} />
        ))}
      </ul>
      {query.isError && (
        <ErrorState testId="ledger-more-error" retryTestId="ledger-more-retry" error={query.error} onRetry={() => { void (query.isFetchNextPageError ? query.fetchNextPage() : query.refetch()) }} />
      )}
      {query.hasNextPage && !query.isFetchNextPageError && (
        <button type="button" data-testid="ledger-more" disabled={query.isFetchingNextPage} onClick={() => { void query.fetchNextPage() }} className="border-border text-text border px-3">
          {query.isFetchingNextPage ? 'Lädt …' : 'Mehr laden'}
        </button>
      )}
      <div
        data-testid="ledger-balance"
        data-numeric
        // Nur am Schreibtisch `sticky`: Unter 768px ist die letzte Zeile eine
        // Karte mit eigenem Storno-Auslöser, und eine am Fuß angeheftete
        // Summenzeile legt sich in kurzen Listen genau darüber — der Auslöser
        // bleibt im Dokument, ist aber unter der Summenzeile nicht mehr
        // antippbar (Spec 04c, Befund „kein ⋯ an der Zahlungszeile"; per
        // `elementFromPoint` an dieser Stelle nachgemessen, nicht vermutet).
        className="border-border bg-surface flex justify-end border-t px-(--page-x) py-2 md:sticky md:bottom-0"
      >
        <span className="text-body text-text font-medium">{query.data.balance.display}</span>
      </div>
    </>
  )
}

function LedgerRowItem({
  row,
  onError,
  onReversed,
}: {
  readonly row: LedgerRow
  readonly onError: (message: string | undefined) => void
  readonly onReversed: (message: string) => void
}) {
  const reverseMutation = useReversePayment()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  // Am Schreibtisch ein eigener Knopf, unter 768px wandert er ins ⋯-Menü der
  // Karte, „damit die Karte nicht zwei konkurrierende Ziele trägt" (Spec 04c,
  // „Schmale Ansicht") — dieselbe Regel wie auf iOS. Über `useIsDesktopViewport`
  // entschieden statt per CSS versteckt: Zwei nur optisch verborgene Kopien mit
  // demselben `data-testid` stünden trotzdem beide im Dokument (`lib/viewport.ts`).
  const isDesktop = useIsDesktopViewport()

  async function handleConfirm() {
    onError(undefined)
    try {
      await reverseMutation.mutateAsync({ paymentId: row.id })
      setIsDialogOpen(false)
      onReversed('Zahlung storniert.')
    } catch (caught) {
      setIsDialogOpen(false)
      onError(caught instanceof ApiError ? caught.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  return (
    <li
      data-testid={`ledger-row-${row.id}`}
      className="border-border flex flex-col gap-1 border-b px-(--page-x) py-2 last:border-b-0 md:flex-row md:items-center md:gap-3 md:py-1.5"
    >
      <span className="text-label text-muted md:w-24">{formatDate(row.on)}</span>
      <span className={`text-body text-text flex flex-wrap items-center gap-1.5 md:flex-1 ${row.reversed ? 'line-through' : ''}`}>
        {row.text}
        {row.reversed && <span className="text-label text-muted font-normal no-underline">storniert</span>}
      </span>
      {/* Soll/Haben unter 768px beschriftet — sonst sehen zwei nackte Beträge
       * untereinander gleich aus (Spec 04c, Befund „Soll und Haben sind auf
       * 380px unbeschriftet"). Am Schreibtisch übernimmt der Spaltenkopf das,
       * `md:contents` löst den Wrapper dort auf (dasselbe Muster wie
       * `UnallocatedList.tsx::UnallocatedRow`). Nur eine der beiden Spalten
       * ist je Zeile belegt (`row_kind` „charge" trägt Soll, „payment" trägt
       * Haben — auch eine Stornozahlung, negativ, im Soll, Spec 04c „Das
       * Mietkonto hängt am Mieter"), die leere bleibt deshalb ganz weg statt
       * eine Beschriftung ohne Wert zu zeigen. */}
      {row.debit_display !== null && (
        <span className="flex items-center justify-between gap-2 md:contents">
          <span className="text-label text-muted md:hidden">Soll</span>
          <span data-numeric className="text-body text-text md:w-24">
            {row.debit_display}
          </span>
        </span>
      )}
      {row.credit_display !== null && (
        <span className="flex items-center justify-between gap-2 md:contents">
          <span className="text-label text-muted md:hidden">Haben</span>
          <span data-numeric className="text-body text-text md:w-24">
            {row.credit_display}
          </span>
        </span>
      )}
      <span data-numeric className="text-body text-text font-medium md:w-24">
        {row.balance_display}
      </span>

      {row.can_reverse && (
        <>
          {isDesktop ? (
            <button
              type="button"
              data-testid={`ledger-reverse-${row.id}`}
              onClick={() => {
                setIsDialogOpen(true)
              }}
              className={`text-text print:hidden self-start px-3 md:self-auto ${FIELD_CLASS}`}
            >
              Stornieren
            </button>
          ) : (
            <LedgerRowMenu
              rowId={row.id}
              onReverseClick={() => {
                setIsDialogOpen(true)
              }}
            />
          )}

          <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <AlertDialogContent style={{ '--tw-enter-scale': 1, '--tw-exit-scale': 1 } as CSSProperties}>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-section font-(--font-weight-section) text-text">
                  Zahlung stornieren?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-label text-muted">
                  Diese Zahlung stornieren? Sie bleibt im Mietkonto stehen, ein Gegensatz gleicht sie aus. Danach kann
                  neu erfasst werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="ledger-reverse-cancel">Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="ledger-reverse-confirm"
                  disabled={reverseMutation.isPending}
                  onClick={() => void handleConfirm()}
                >
                  Stornieren
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </li>
  )
}

/** Das ⋯-Menü der Karte unter 768px (Spec 04c, „Schmale Ansicht") — geteilte
 * Form mit `AccountMenu` (`router.tsx`) über `components/PopoverMenu.tsx`.
 * `ledger-actions-<id>` am Auslöser selbst (zuvor nur `aria-label`, Befund W5:
 * ohne `data-testid` erreicht die E2E-Suite ihn nicht) — vom
 * `design-reviewer` als Name vorgeschlagen (Bericht 16, W-D9), damit iOS
 * später denselben Bezeichner als `accessibilityIdentifier` tragen kann. Der
 * Menüpunkt behält `ledger-reverse-<id>`, denselben Bezeichner wie der
 * Schreibtisch-Knopf (Spec 04c, „Storno auf einer Zeile"). */
function LedgerRowMenu({ rowId, onReverseClick }: { readonly rowId: string; readonly onReverseClick: () => void }) {
  return (
    <PopoverMenu
      wrapperClassName="self-end"
      triggerTestId={`ledger-actions-${rowId}`}
      triggerLabel="Weitere Aktionen"
      triggerClassName="text-muted hover:text-text flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center"
      menuClassName="border-border bg-surface rounded-card absolute right-0 z-50 mt-1 w-40 border"
      trigger={<MoreHorizontal aria-hidden="true" className="size-4" />}
    >
      {(close) => (
        <button
          type="button"
          role="menuitem"
          data-testid={`ledger-reverse-${rowId}`}
          onClick={() => {
            close()
            onReverseClick()
          }}
          className="text-body text-status-overdue hover:bg-bg flex min-h-(--tap-min) w-full items-center px-3 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
        >
          Stornieren
        </button>
      )}
    </PopoverMenu>
  )
}
